// google_maps_service.js
export default class GoogleMapsService {
  constructor(apiKey) {
    //console.log("Initializing GoogleMapsService with API key");
    this.apiKey = apiKey;
    this.autocomplete = null;
    this.map = null;
    this.marker = null;
    this.directionsService = null;
    this.directionsRenderer = null;
    this.markers = [];
    this.businessAddress = null;
  }

  setBusinessAddress(address) {
    this.businessAddress = address;
  }


  //Adds teh google maps javascript api script to the head of the html doc. not really needed switch to embed map
  async loadGoogleMapsAPI() {
    //console.log("Loading Google Maps API...");
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
        //console.log("Google Maps API already loaded");
        resolve();
      } else {
        //console.log("Injecting Google Maps script...");
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,marker`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          //console.log("Google Maps API loaded successfully");
          resolve();
        };
        script.onerror = (error) => {
          console.error("Failed to load Google Maps API:", error);
          reject(error);
        };
        document.head.appendChild(script);
      }
    });
  }

  initAutocomplete(inputElement, callback) {
    this.autocomplete = new google.maps.places.Autocomplete(inputElement, {
      fields: ["address_components", "geometry"]
    });

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace();
      callback(place);
    });
  }

  initMap(mapElement, options = {}) {
    //console.log("Initializing map...");
    const defaultOptions = {
      center: { lat: 37.4221, lng: -122.0841 },
      fullscreenControl: true,
      mapTypeControl: true,
      streetViewControl: true,
      zoom: 11,
      zoomControl: true,
      maxZoom: 22,
      mapId: "2709d22832c75bd9",
    }

    try {
      this.map = new google.maps.Map(mapElement, { ...defaultOptions, ...options });
      this.marker = new google.maps.marker.AdvancedMarkerElement({ map: this.map });
      return {map: this.map, marker: this.marker};

    } catch (e) {
      console.error("Error initializing map:", e);
    }


  }

  setMapCenter(lat, lng, zoom = 11) {
    if (this.map) {
      this.map.setCenter({ lat, lng });
      this.map.setZoom(zoom);
    }
  }

  setMarkerPosition(lat, lng) {
    if (this.marker) {
      this.marker.position = { lat, lng };
    }
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  }

  addMarker(address, label) {
    this.geocodeAddress(address, (results, status) => {
      if (status === 'OK' && results[0]) {
        const marker = new google.maps.Marker({
          map: this.map,
          position: results[0].geometry.location,
          label: label.toString()
        });
        this.markers.push(marker);
      }
    });
  }

  fitBounds(bounds) {
    if (this.map) {
      this.map.fitBounds(bounds);
    }
  }

  initDirectionsService() {
    this.directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer();
    this.directionsRenderer.setMap(this.map);
  }



  callDistanceMatrixAPI(origins, destinations, measurementUnit) {
    return new Promise((resolve, reject) => {
      const service = new google.maps.DistanceMatrixService();
      const request = {
        origins: origins,
        destinations: destinations,
        travelMode: "DRIVING",
        unitSystem:
          measurementUnit === "imperial"
            ? google.maps.UnitSystem.IMPERIAL
            : google.maps.UnitSystem.METRIC,
      };

      const timeout = setTimeout(() => {
        reject(new Error("Distance Matrix API call timed out"));
      }, 15000);

      service.getDistanceMatrix(request, (response, status) => {
        clearTimeout(timeout);
        if (status === "OK") {
          resolve(response);
        } else {
          reject(new Error(`Error with Distance Matrix API: ${status}`));
        }
      });
    });
  }

  geocodeAddress(address, callback) {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      callback(results, status);
    });
  }

  calculateAndDisplayRoute(addresses, startingPoint = 'business', businessAddress, measurementUnit) {
    //console.log("Calculating route with:", { addresses, startingPoint, businessAddress, measurementUnit });

    if (addresses.length < 1) {
      //console.log("No addresses provided for route calculation");
      return;
    }

    let origin, destination, waypoints;

    if (startingPoint === 'business' && businessAddress) {
      //console.log("Using business address as starting point");
      origin = businessAddress;
      waypoints = addresses.map(address => ({
        location: `${address.address_line}, ${address.city}, ${address.state}, ${address.postal_code}`,
        stopover: true
      }));
      destination = waypoints.pop().location;
    } else {
      //console.log("Using first address as starting point");
      origin = `${addresses[0].address_line}, ${addresses[0].city}, ${addresses[0].state}, ${addresses[0].postal_code}`;
      waypoints = addresses.slice(1, -1).map(address => ({
        location: `${address.address_line}, ${address.city}, ${address.state}, ${address.postal_code}`,
        stopover: true
      }));
      destination = `${addresses[addresses.length - 1].address_line}, ${addresses[addresses.length - 1].city}, ${addresses[addresses.length - 1].state}, ${addresses[addresses.length - 1].postal_code}`;
    }

    //console.log("Route configuration:", { origin, destination, waypoints });

    return this.calculateRoute(origin, destination, waypoints, measurementUnit, (response) => {
      if (response) {
        this.showTextDirections(response);
        const { totalDistance, totalTime } = this.calculateTotalDistanceAndTime(response, measurementUnit);
        return { response, totalDistance, totalTime };
      }
    });
  }



  displayRoute(routeResponse) {
    if (!this.directionsRenderer) {
      console.error("Directions renderer not initialized");
      return;
    }
    
    try {
      this.directionsRenderer.setDirections(routeResponse);
      if (routeResponse.routes && routeResponse.routes[0]) {
        this.fitBounds(routeResponse.routes[0].bounds);
      }
    } catch (error) {
      console.error("Error displaying cached route:", error);
    }
  }

  calculateRoute(origin, destination, waypoints, measurementUnit, callback) {
    //console.log("Calculating route with:", { origin, destination, waypoints, measurementUnit });

    if (!this.directionsService) {
      console.error("Directions service not initialized");
      return Promise.reject("Directions service not initialized");
    }

    return new Promise((resolve, reject) => {
      //console.log("Making directions request...");
      this.directionsService.route(
        {
          origin: origin,
          destination: destination,
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === "OK") {
            this.directionsRenderer.setDirections(response);
            this.fitBounds(response.routes[0].bounds);
            const result = callback(response);
            resolve(result);
          } else {
            console.error("Directions request failed due to " + status);
            reject(status);
          }
        }
      );
    });
  }

  calculateTotalDistanceAndTime(response, measurementUnit) {
    let totalDistance = 0;
    let totalTime = 0;

    const route = response.routes[0];
    route.legs.forEach((leg) => {
      totalDistance += leg.distance.value;
      totalTime += leg.duration.value;
    });

    // Convert distance to miles or kilometers
    if (measurementUnit === "imperial") {
      totalDistance = (totalDistance / 1609.344).toFixed(2) + " miles";
    } else {
      totalDistance = (totalDistance / 1000).toFixed(2) + " km";
    }

    // Convert time to a readable format
    totalTime = this.formatTime(Math.round(totalTime / 60));

    return { totalDistance, totalTime };
  }

  formatTime(totalMinutes) {
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    let timeString = "";
    if (days > 0) {
      const _d = days > 1 ? 'days' : 'day';
      timeString += `${days} ${_d} `;
    }
    if (hours > 0 || days > 0) {
      const _h = hours > 1 ? 'hours' : 'hour';
      timeString += `${hours} ${_h} `;
    }
    if (minutes > 0 || hours > 0 || days > 0) {
      const _m = minutes > 1 ? 'mins' : 'min';
      timeString += `${minutes} ${_m}`;
    }
    return timeString.trim();
  }

  showTextDirections(response) {
    const directionsPanel = document.getElementById('directionsPanel');
    if (!directionsPanel) return;

    directionsPanel.innerHTML = "";
    const route = response.routes[0];

    for (let i = 0; i < route.legs.length; i++) {
      const leg = route.legs[i];
      const legElement = document.createElement("div");
      legElement.classList.add("leg", "mb-4", "p-4", "border", "rounded-lg", "shadow-sm");
      legElement.innerHTML = `<h3 class="text-lg font-semibold mb-2">Leg ${i + 1}: ${leg.start_address} to ${leg.end_address}</h3>`;

      for (let j = 0; j < leg.steps.length; j++) {
        const step = leg.steps[j];
        const stepElement = document.createElement("p");
        stepElement.innerHTML = `${j + 1}. ${step.instructions} (${step.distance.text})`;
        legElement.appendChild(stepElement);
      }

      directionsPanel.appendChild(legElement);
    }
  }
}
